import { Formik, FormikValues } from "formik";

import { useGetPricingByIdQuery, useUpdatePricingMutation } from "@sso/apis/pricing";
import { useGetSoftwareLicenceById } from "@sso/apis/softwareLicences/hooks";
import { useAppSelector } from "@sso/hooks/useRedux";
import { selectDialog } from "@sso/store/slices/dialog";
import { selectPricingSoftwareLicenceId } from "@sso/store/slices/pricing";
import {
  LicenceType,
  PricingKeys,
  PricingRuleCreateRequest,
  PricingRuleKeys,
  PricingSummaryKeys,
  PricingUpdateRequest,
} from "@sso/types";
import { CustomForm } from "@sso/ui/CustomForm";

import { DialogActionButtons, DialogBody, DialogHeader } from "../components";
import EditMultiCurrencyPricingForm from "./editMultiCurrencyForm";
import { editMultiCurrencyValidation } from "./schema";

const EditMultiCurrencyPricing = () => {
  const { id } = useAppSelector(selectDialog);
  if (!id) {
    throw new Error("PricingEditDialog: id is null");
  }

  const { data, isSuccess } = useGetPricingByIdQuery(id);
  const [updatePricing, { isLoading: updateLoading }] = useUpdatePricingMutation();
  const softwareLicenceId = useAppSelector(selectPricingSoftwareLicenceId);
  const softwareLicence = useGetSoftwareLicenceById(softwareLicenceId);

  const rules =
    data?.pricingRules?.map(rule => ({
      [PricingRuleKeys.MIN_RANGE]: rule[PricingRuleKeys.MIN_RANGE],
      [PricingRuleKeys.MAX_RANGE]: rule[PricingRuleKeys.MAX_RANGE],
      [PricingRuleKeys.BILLABLE_OPERATION_ID]: rule[PricingRuleKeys.BILLABLE_OPERATION_ID],
      [PricingRuleKeys.PRICE]: rule[PricingRuleKeys.PRICE],
      [PricingRuleKeys.CURRENCY_CODE]: rule.currency?.code,
      [PricingRuleKeys.CURRENCY]: rule.currency,
      isLast: rule[PricingRuleKeys.MIN_RANGE] != null && rule[PricingRuleKeys.MAX_RANGE] == null,
    })) || [];

  const initialValues = {
    currencyCode: "GBP",
    pricingRules: rules as PricingRuleCreateRequest[],
    apiPricingRules: [],
    softwareLicenceId: softwareLicenceId,
    licenceType: softwareLicence ? softwareLicence.licenceType : "",
    pricingId: data ? data[PricingSummaryKeys.PRICING_ID] : "",
    dateOfEffect: data ? data.dateOfEffect : "",
  };

  const onSubmit = (values: FormikValues) => {
    if (softwareLicence!.licenceType === LicenceType.API) {
      values[PricingKeys.PRICING_RULES] = values.apiPricingRules?.reduce((acc: any, curr: any) => {
        const pricingRules = curr.pricingRules.map((rule: any) => {
          return { ...rule, billableOperationId: curr.billableOperationId };
        });
        return [...acc, ...pricingRules];
      }, []);
    }

    var request = {
      [PricingSummaryKeys.PRICING_ID]: values[PricingSummaryKeys.PRICING_ID],
      [PricingSummaryKeys.DATE_OF_EFFECT]: values[PricingSummaryKeys.DATE_OF_EFFECT],
      [PricingKeys.PRICING_RULES]: values[PricingKeys.PRICING_RULES],
      softwareLicenceId: values.softwareLicenceId,
    } as PricingUpdateRequest;

    updatePricing(request);
  };

  return (
    <>
      <DialogHeader title={"Edit Multi Currency Pricing"} />
      <DialogBody>
        {data && softwareLicence && rules && (
          <Formik onSubmit={onSubmit} initialValues={initialValues} validationSchema={editMultiCurrencyValidation}>
            {({ handleSubmit }) => (
              <CustomForm onSubmit={handleSubmit} width="100%" overflow="hidden">
                <DialogBody>
                  {isSuccess && data && softwareLicence && (
                    <>
                      <EditMultiCurrencyPricingForm rules={rules} />
                    </>
                  )}
                </DialogBody>
                <DialogActionButtons loading={updateLoading} buttonTitle={"Edit"} />
              </CustomForm>
            )}
          </Formik>
        )}
      </DialogBody>
    </>
  );
};

export default EditMultiCurrencyPricing;
